import { useEffect, useState, useRef, use } from "react";
import "./style.css";
import useFetchTopics from "../../hooks/useFetchTopics";
import { BookLoader } from "../../components/loader";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

import topicButtonsound from "../../assets/audios/topics.wav";

import {
  playSpeech,
  playSpeechTTS,
  stopSpeechTTS,
} from "../../utils/playSpeech";
import { playSpeechKokoro } from "../../utils/playSpeechKokoro";
import { stopSpeechKokoro } from "../../utils/playSpeechKokoro";
function InlineSVG({ url, topicsName, onMouseEnter, onMouseLeave }) {
  const [svgContent, setSvgContent] = useState(null);

  useEffect(() => {
    fetch(url)
      .then((res) => res.text())
      .then(setSvgContent)
      .catch((err) => console.error("Error loading SVG:", err));
  }, [url]);

  useEffect(() => {
    if (!svgContent) return;

    const container = document.getElementById(
      `inline-svg-container-${topicsName}`,
    );
    if (!container) return;

    const allImages = container.querySelectorAll("image");
    if (!allImages.length) return;

    allImages.forEach((img) => {
      img.style.transition = "transform 0.5s ease-in-out";
      img.style.transformOrigin = "center";
      img.style.transformBox = "fill-box";
    });

    const handleMouseEnter = () => {
      allImages.forEach((img) => {
        img.setAttribute("transform", "translate(2,-5) scale(1.05)");
      });
      onMouseEnter?.(); // Call the voice handler
    };

    const handleMouseLeave = () => {
      allImages.forEach((img) => {
        img.setAttribute("transform", "translate(0,0) scale(1)");
      });
      onMouseLeave?.(); // Call the voice handler
    };

    container.addEventListener("mouseenter", handleMouseEnter);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mouseenter", handleMouseEnter);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [svgContent, topicsName, onMouseEnter, onMouseLeave]);

  if (!svgContent) return null;

  return (
    <div
      id={`inline-svg-container-${topicsName}`}
      className="topic-svg-container"
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
}

function TopicsPage({ handleClick }) {
  const navigate = useNavigate();
  const { breadcrumbs, setBreadcrumbs } = useAuth();
  const [topics, loading] = useFetchTopics();
  const topicsSectionRef = useRef(null);
  const nextBtnRef = useRef(null);
  const prevBtnRef = useRef(null);
  const currentIndexRef = useRef(0);
  const [visibleCards, setVisibleCards] = useState(5);
  const [showLoader, setShowLoader] = useState(true);
  const [ttsError, setTtsError] = useState(null);
  const [hoverEnabled, setHoverEnabled] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(false), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      playSpeechKokoro("Select a Topic to Learn", setTtsError);
    }, 1000);

    return () => {
      clearTimeout(timer);
      stopSpeechKokoro();
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setHoverEnabled(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleTopicHover = (topicName) => {
    if (!hoverEnabled) return;

    stopSpeechKokoro(); // Stop any current speech
    playSpeechKokoro(topicName, setTtsError);
  };
  // console.log("topics", useFetchTopics());
  // // Handle responsive visible cards based on screen width
  // useEffect(() => {
  //   const handleResize = () => {
  //     const width = window.innerWidth;
  //     let cards;

  //     if (width >= 1600) {
  //       cards = 5;
  //     } else if (width >= 1321) {
  //       cards = 4;
  //     } else if (width >= 1061) {
  //       cards = 3;
  //     } else if (width >= 768) {
  //       cards = 2;
  //     } else {
  //       cards = 1;
  //     }

  //     setVisibleCards(cards);
  //     currentIndexRef.current = 0;
  //   };

  //   handleResize();
  //   window.addEventListener("resize", handleResize);
  //   return () => window.removeEventListener("resize", handleResize);
  // }, []);

  useEffect(() => {
    const section = topicsSectionRef.current;
    const nextBtn = nextBtnRef.current;
    const prevBtn = prevBtnRef.current;

    if (
      !section ||
      !nextBtn ||
      !prevBtn ||
      loading ||
      showLoader ||
      topics.length === 0
    ) {
      return;
    }

    const timer = setTimeout(() => {
      const cards = section.querySelectorAll(".topic-card");

      if (cards.length === 0) return;

      const cardWidth = cards[0].offsetWidth + 20;

      if (cards.length <= visibleCards) {
        section.style.justifyContent = "center";
        nextBtn.disabled = true;
        prevBtn.disabled = true;
      } else {
        section.style.justifyContent = "flex-start";
      }

      function updateButtons() {
        prevBtn.disabled = currentIndexRef.current <= 0;
        nextBtn.disabled =
          currentIndexRef.current + visibleCards >= cards.length;
      }

      const handleNext = () => {
        if (currentIndexRef.current + visibleCards < cards.length) {
          currentIndexRef.current = Math.min(
            currentIndexRef.current + visibleCards,
            cards.length - visibleCards,
          );
          section.scrollTo({
            left: cardWidth * currentIndexRef.current,
            behavior: "smooth",
          });
          updateButtons();
        }
      };

      const handlePrev = () => {
        currentIndexRef.current = Math.max(
          currentIndexRef.current - visibleCards,
          0,
        );
        section.scrollTo({
          left: cardWidth * currentIndexRef.current,
          behavior: "smooth",
        });
        updateButtons();
      };

      nextBtn.addEventListener("click", handleNext);
      prevBtn.addEventListener("click", handlePrev);

      updateButtons();

      return () => {
        nextBtn.removeEventListener("click", handleNext);
        prevBtn.removeEventListener("click", handlePrev);
      };
    }, 0);

    return () => clearTimeout(timer);
  }, [topics, loading, showLoader, visibleCards]);

  return (
    <div className="topics-content-box">
      <div className="welc-text">
        <h2 className="topic-text">Select a Topic to Learn</h2>
      </div>
      <div className="topics-carousel-wrapper">
        {!loading && !showLoader && (
          <button ref={prevBtnRef} className="scroll-btn topic-prev-btn">
            &#10094;
          </button>
        )}

        {loading || showLoader ? (
          <BookLoader />
        ) : (
          <div className="topics-section" ref={topicsSectionRef}>
            {topics.map((topic, index) => {
              const isSVG = topic.image.endsWith(".svg");
              return (
                <div
                  className="topic-card"
                  key={index}
                  onClick={() => {
                    stopSpeechKokoro(); 

                    const buttonAudio = new Audio(topicButtonsound);
                    buttonAudio
                      .play()
                      .catch((err) => console.error("Audio play failed:", err));

                    handleClick(topic.id, topic.name, `/subtopics`);
                  }}
                  onMouseEnter={() => handleTopicHover(topic.name)}
                >
                  {isSVG ? (
                    <InlineSVG
                      url={topic.image}
                      topicsName={`topic-${index}`}
                      onMouseEnter={() => handleTopicHover(topic.name)}
                    />
                  ) : (
                    <img
                      src={topic.image}
                      alt={topic.name}
                      className="topics-object fallback-background"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
        {!loading && !showLoader && (
          <button ref={nextBtnRef} className="scroll-btn topic-next-btn">
            &#10095;
          </button>
        )}
      </div>
    </div>
  );
}

export default TopicsPage;
